import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Trophy, Lock, Sparkles, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlayer } from "@/context/PlayerContext";
import { getAchievements, claimAchievements } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";
import { toast } from "sonner";
import { playWin } from "@/lib/sound";

export default function Achievements() {
  const { t } = useTranslation();
  const { player, setPlayer } = usePlayer();
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (!player?.id) return;
    getAchievements(player.id).then(setData).catch(() => {});
  }, [player?.id]);

  if (!player) {
    return (
      <div className="min-h-screen hero-radial grain flex items-center justify-center p-6">
        <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
          <Trophy className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <h2 className="font-serif text-2xl font-bold">{t("achievements.no_profile_title")}</h2>
          <Link to="/profile" className="inline-block mt-4 text-amber-300 hover:text-amber-200 font-bold">{t("parental.no_profile_cta")}</Link>
        </div>
      </div>
    );
  }

  const claimed = new Set(player.claimed_achievements || []);
  const claimable = (data?.achievements || []).filter((a) => a.unlocked && !claimed.has(a.id) && a.shards_bonus > 0);
  const totalBonus = claimable.reduce((s, a) => s + a.shards_bonus, 0);

  const claimAll = async () => {
    if (claimable.length === 0) return;
    setClaiming(true);
    try {
      const updated = await claimAchievements(player.id, claimable.map((a) => a.id));
      setPlayer(updated);
      playWin();
      toast.success(t("achievements.claim_success", { n: totalBonus }));
      const r = await getAchievements(player.id);
      setData(r);
    } catch (e) {
      toast.error(t("achievements.claim_error"));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/levels" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-levels">
          <ChevronLeft className="w-4 h-4" /> {t("common.back")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Trophy className="w-5 h-5 text-amber-300" /> {t("achievements.header")}
        </div>
        <SoundToggle />
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-24" data-testid="achievements-content">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("achievements.kicker")}</div>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("achievements.title_1")}<span className="italic text-amber-300">{player.name}</span>
          </h2>
          <p className="mt-2 text-stone-300 max-w-2xl">{t("achievements.subtitle")}</p>

          {data && (
            <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="ach-progress">
              <div className="glass rounded-full px-4 py-2 flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-300" />
                <span className="font-serif text-lg font-black text-amber-200">{data.unlocked_count}</span>
                <span className="text-stone-400 text-sm">/ {data.total} {t("achievements.unlocked")}</span>
              </div>
              {claimable.length > 0 && (
                <Button
                  onClick={claimAll}
                  disabled={claiming}
                  className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-5"
                  data-testid="btn-claim-all"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t("achievements.claim_cta", { n: totalBonus })}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {(data?.achievements || []).map((a, idx) => {
              const isNewClaimable = a.unlocked && !claimed.has(a.id) && a.shards_bonus > 0;
              const isClaimed = claimed.has(a.id);
              return (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className={`glass rounded-3xl p-5 relative overflow-hidden transition-all ${
                    a.unlocked ? "border-amber-400/40" : "opacity-45"
                  }`}
                  data-testid={`ach-${a.id}`}
                  data-unlocked={a.unlocked ? "1" : "0"}
                >
                  {isNewClaimable && (
                    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-fuchsia-500 text-stone-950 font-bold px-2 py-0.5 rounded-full animate-pulse">
                      {t("achievements.new")}
                    </span>
                  )}
                  {isClaimed && a.shards_bonus > 0 && (
                    <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 font-bold px-2 py-0.5 rounded-full">
                      {t("achievements.claimed")}
                    </span>
                  )}
                  <div className="flex items-start gap-4">
                    <div className={`text-5xl ${a.unlocked ? "shard-glow" : "grayscale opacity-60"}`} aria-hidden>
                      {a.unlocked ? a.icon : "🔒"}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-widest text-amber-300/80 font-bold">
                        {t(`achievements.list.${a.id}.kicker`)}
                      </div>
                      <div className="mt-1 font-serif text-xl font-bold text-stone-50">
                        {t(`achievements.list.${a.id}.title`)}
                      </div>
                      <p className="mt-2 text-sm text-stone-300 leading-relaxed">
                        {t(`achievements.list.${a.id}.desc`)}
                      </p>
                      {a.shards_bonus > 0 && (
                        <div className="mt-3 flex items-center gap-1 text-xs text-amber-200 font-bold">
                          <Sparkles className="w-3 h-3" /> +{a.shards_bonus} {t("levels.shards")}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {data?.achievements?.length === 0 && (
          <div className="text-center text-stone-400 py-12">
            <Lock className="w-10 h-10 mx-auto mb-3 opacity-50" />
            {t("achievements.empty")}
          </div>
        )}
      </main>
    </div>
  );
}
